// A pure-JavaScript BLAKE3 hash. No dependency.
//
// The store addresses every block by its BLAKE3 hash, so the client must hash
// each file chunk before it sends it. `@kinedb/client` takes no runtime
// dependency, so the hash lives here.
//
// This module implements the unkeyed "hash" mode only. It follows the BLAKE3
// reference implementation, including the full chunk tree, so it is correct
// for an input of any length.
//
//   import { hashHex } from './blake3.js';
//   hashHex(new TextEncoder().encode('abc'));
//   // '6437b3ac38465133ffb63b75273a8db548c558465d79db03fd359c6cd5bd9d85'

export const OUT_LEN = 32;
export const BLOCK_LEN = 64;
export const CHUNK_LEN = 1024;

const CHUNK_START = 1 << 0;
const CHUNK_END = 1 << 1;
const PARENT = 1 << 2;
const ROOT = 1 << 3;

const IV = Uint32Array.from([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
  0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);

const MSG_PERMUTATION = [2, 6, 3, 10, 7, 0, 4, 13, 1, 11, 12, 5, 9, 14, 15, 8];

function rotr(x, n) {
  return ((x >>> n) | (x << (32 - n))) >>> 0;
}

// The BLAKE3 quarter-round. It mutates the state in place.
function g(s, a, b, c, d, mx, my) {
  s[a] = (s[a] + s[b] + mx) >>> 0;
  s[d] = rotr((s[d] ^ s[a]) >>> 0, 16);
  s[c] = (s[c] + s[d]) >>> 0;
  s[b] = rotr((s[b] ^ s[c]) >>> 0, 12);
  s[a] = (s[a] + s[b] + my) >>> 0;
  s[d] = rotr((s[d] ^ s[a]) >>> 0, 8);
  s[c] = (s[c] + s[d]) >>> 0;
  s[b] = rotr((s[b] ^ s[c]) >>> 0, 7);
}

// Run the 7 rounds over one 64-byte block. It returns 16 words.
function compress(cv, blockWords, counter, blockLen, flags) {
  const s = new Uint32Array(16);
  s[0] = cv[0]; s[1] = cv[1]; s[2] = cv[2]; s[3] = cv[3];
  s[4] = cv[4]; s[5] = cv[5]; s[6] = cv[6]; s[7] = cv[7];
  s[8] = IV[0]; s[9] = IV[1]; s[10] = IV[2]; s[11] = IV[3];
  s[12] = counter >>> 0;
  s[13] = Math.floor(counter / 4294967296) >>> 0;
  s[14] = blockLen >>> 0;
  s[15] = flags >>> 0;

  let m = blockWords;
  for (let r = 0; r < 7; r += 1) {
    // The column round.
    g(s, 0, 4, 8, 12, m[0], m[1]);
    g(s, 1, 5, 9, 13, m[2], m[3]);
    g(s, 2, 6, 10, 14, m[4], m[5]);
    g(s, 3, 7, 11, 15, m[6], m[7]);
    // The diagonal round.
    g(s, 0, 5, 10, 15, m[8], m[9]);
    g(s, 1, 6, 11, 12, m[10], m[11]);
    g(s, 2, 7, 8, 13, m[12], m[13]);
    g(s, 3, 4, 9, 14, m[14], m[15]);
    if (r < 6) {
      const next = new Uint32Array(16);
      for (let i = 0; i < 16; i += 1) next[i] = m[MSG_PERMUTATION[i]];
      m = next;
    }
  }

  for (let i = 0; i < 8; i += 1) {
    s[i] = (s[i] ^ s[i + 8]) >>> 0;
    s[i + 8] = (s[i + 8] ^ cv[i]) >>> 0;
  }
  return s;
}

// Read a 64-byte block as 16 little-endian words.
function wordsFromBlock(block) {
  const w = new Uint32Array(16);
  for (let i = 0; i < 16; i += 1) {
    const o = i * 4;
    w[i] = ((block[o] | (block[o + 1] << 8) | (block[o + 2] << 16) | (block[o + 3] << 24)) >>> 0);
  }
  return w;
}

// One compression that is not run yet. A node gives a CV or the root bytes.
class Output {
  constructor(cv, blockWords, counter, blockLen, flags) {
    this.cv = cv;
    this.blockWords = blockWords;
    this.counter = counter;
    this.blockLen = blockLen;
    this.flags = flags;
  }

  chainingValue() {
    return compress(this.cv, this.blockWords, this.counter, this.blockLen, this.flags)
      .slice(0, 8);
  }

  rootBytes(length) {
    const out = new Uint8Array(Math.ceil(length / 64) * 64);
    let counter = 0;
    let pos = 0;
    while (pos < length) {
      const words = compress(
        this.cv, this.blockWords, counter, this.blockLen, this.flags | ROOT,
      );
      for (let i = 0; i < 16; i += 1) {
        const w = words[i];
        out[pos] = w & 0xff;
        out[pos + 1] = (w >>> 8) & 0xff;
        out[pos + 2] = (w >>> 16) & 0xff;
        out[pos + 3] = (w >>> 24) & 0xff;
        pos += 4;
      }
      counter += 1;
    }
    return out.slice(0, length);
  }
}

function parentOutput(left, right, key, flags) {
  const words = new Uint32Array(16);
  words.set(left, 0);
  words.set(right, 8);
  return new Output(key, words, 0, BLOCK_LEN, PARENT | flags);
}

function parentCv(left, right, key, flags) {
  return parentOutput(left, right, key, flags).chainingValue();
}

// The state of one 1024-byte chunk of the BLAKE3 tree.
class ChunkState {
  constructor(keyWords, chunkCounter, flags) {
    this.cv = keyWords;
    this.chunkCounter = chunkCounter;
    this.block = new Uint8Array(BLOCK_LEN);
    this.blockLen = 0;
    this.blocksCompressed = 0;
    this.flags = flags;
  }

  length() {
    return BLOCK_LEN * this.blocksCompressed + this.blockLen;
  }

  startFlag() {
    return this.blocksCompressed === 0 ? CHUNK_START : 0;
  }

  update(data) {
    let pos = 0;
    while (pos < data.length) {
      if (this.blockLen === BLOCK_LEN) {
        this.cv = compress(
          this.cv,
          wordsFromBlock(this.block),
          this.chunkCounter,
          BLOCK_LEN,
          this.flags | this.startFlag(),
        ).slice(0, 8);
        this.blocksCompressed += 1;
        this.block = new Uint8Array(BLOCK_LEN);
        this.blockLen = 0;
      }
      const take = Math.min(BLOCK_LEN - this.blockLen, data.length - pos);
      this.block.set(data.subarray(pos, pos + take), this.blockLen);
      this.blockLen += take;
      pos += take;
    }
  }

  output() {
    return new Output(
      this.cv,
      wordsFromBlock(this.block),
      this.chunkCounter,
      this.blockLen,
      this.flags | this.startFlag() | CHUNK_END,
    );
  }
}

// A streaming BLAKE3 hasher in the unkeyed mode.
export class Hasher {
  constructor() {
    this.keyWords = IV.slice(0, 8);
    this.chunkState = new ChunkState(IV.slice(0, 8), 0, 0);
    this.cvStack = [];
    this.flags = 0;
  }

  #addChunkChainingValue(newCv, totalChunks) {
    let cv = newCv;
    let total = totalChunks;
    while (total % 2 === 0) {
      cv = parentCv(this.cvStack.pop(), cv, this.keyWords, this.flags);
      total /= 2;
    }
    this.cvStack.push(cv);
  }

  // Fold `data` into the hash. Call it any number of times.
  update(data) {
    const view = data instanceof Uint8Array ? data : new Uint8Array(data);
    let pos = 0;
    while (pos < view.length) {
      if (this.chunkState.length() === CHUNK_LEN) {
        const chunkCv = this.chunkState.output().chainingValue();
        const total = this.chunkState.chunkCounter + 1;
        this.#addChunkChainingValue(chunkCv, total);
        this.chunkState = new ChunkState(this.keyWords, total, this.flags);
      }
      const take = Math.min(CHUNK_LEN - this.chunkState.length(), view.length - pos);
      this.chunkState.update(view.subarray(pos, pos + take));
      pos += take;
    }
    return this;
  }

  // Return the digest of every byte fed in so far.
  finalize(length = OUT_LEN) {
    let output = this.chunkState.output();
    for (let i = this.cvStack.length - 1; i >= 0; i -= 1) {
      output = parentOutput(
        this.cvStack[i], output.chainingValue(), this.keyWords, this.flags,
      );
    }
    return output.rootBytes(length);
  }
}

// Return the BLAKE3 digest of `data` as a Uint8Array.
export function blake3(data, length = OUT_LEN) {
  return new Hasher().update(data).finalize(length);
}

// Return the BLAKE3 digest of `data` as lower-case hex.
export function hashHex(data, length = OUT_LEN) {
  const bytes = blake3(data, length);
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}
